import { useLazyQuery, useMutation } from '@collinsonx/utils/apollo';
import type { LinkedAccount } from '@collinsonx/utils/generatedTypes/graphql';
import { updateConsumer } from '@collinsonx/utils/mutations';
import {
  consumerIsValid,
  findLinkedAccount,
  getMembership,
  setItem,
} from '@lib';
import {
  LOGOUT_REASON,
  sendInfoLog,
  Unknown,
  useLogStorage,
} from '@lib/logging';
import { BookingInfo } from 'config/datadog';
import {
  authenticateToContinueSession,
  isPageProtected,
} from 'config/navigation';
import { IS_USER_VALIDATED, USER_ID } from 'config/storage';
import { DomainContext } from 'context/DomainContext';
import { getBookingConsumerByID } from 'gql-queries';
import { useContext, useRef } from 'react';

import useGetUserSession from './useGetUserSession';
import usePayload from './usePayload';

export const useHandleUserValidation = () => {
  const { isUserValidated, setIsUserValidated } = useContext(DomainContext);
  const { sessionLoading, userId, userIsSignedIn } = useGetUserSession();
  const inFlightRef = useRef<null | Promise<void>>(null);

  const {
    jwt,
    locale,
    matchedAccount,
    membership,
    payload,
    setConsumerData,
    setMatchedAccount,
    setMatchedMembership,
  } = usePayload();

  const isProtectedPage = isPageProtected();

  const { getUserAccountData, setClientData, setUserAccountData } =
    useLogStorage();

  const [fetchConsumer, { error: fetchConsumerError }] = useLazyQuery(
    getBookingConsumerByID
  );
  const [updateConsumerCall] = useMutation(updateConsumer);

  const hasUserAccount = matchedAccount?.id || membership?.id;

  // Rematch while matched account/membership is missing so refresh (hydrated
  // IS_USER_VALIDATED, empty React account state) can restore via ID fallback.
  // When OTP already seeded matchedAccount, hasUserAccount short-circuits.
  const shouldValidateUser = [
    isProtectedPage,
    !sessionLoading,
    payload,
    jwt,
    !hasUserAccount,
  ].every(Boolean);

  const userValidationDeps = [isProtectedPage, userIsSignedIn];

  const runUserValidation = async () => {
    if (!payload) return;

    if (!userIsSignedIn || !userId) {
      await authenticateToContinueSession({
        reason: LOGOUT_REASON.AUTHENTICATED_USER.INVALID_SESSION,
      });
      return;
    }

    const consumerResponse = await fetchConsumer({
      variables: {
        getConsumerById: userId,
      },
    });

    const consumer = consumerResponse.data?.getConsumerByID;

    if (!consumerIsValid(consumer)) {
      await authenticateToContinueSession({
        reason: LOGOUT_REASON.AUTHENTICATED_USER.INVALID_CONSUMER,
      });
      return;
    }

    if (locale !== consumer.locale) {
      sendInfoLog(BookingInfo.UPDATE_CONSUMER, {
        consumerLocale: consumer.locale,
        payloadLocale: locale,
        reason: 'Locale updated',
      });
      await updateConsumerCall({
        variables: {
          consumerInput: {
            emailAddress: consumer.emailAddress,
            locale,
          },
        },
      });
    }

    setItem(USER_ID, userId);
    setConsumerData(consumerResponse.data);

    let linkedAccount = findLinkedAccount(payload, consumer.linkedAccounts);

    const membershipFound = getMembership(payload, consumer.memberships);

    if (membershipFound)
      sendInfoLog(BookingInfo.USER_VALIDATION_MEMBERSHIP_FOUND);
    if (linkedAccount)
      sendInfoLog(BookingInfo.USER_VALIDATION_LINKED_ACCOUNT_FOUND);

    if (!membershipFound && !linkedAccount) {
      const { linkedAccountID } = getUserAccountData();
      const storedLinkedAccountId =
        linkedAccountID && linkedAccountID !== Unknown
          ? linkedAccountID
          : undefined;

      linkedAccount =
        consumer.linkedAccounts?.find(
          (account: LinkedAccount) => account.id === storedLinkedAccountId
        ) ?? null;

      if (linkedAccount) {
        sendInfoLog(BookingInfo.USER_VALIDATION_LINKED_ACCOUNT_FOUND);
      }
    }

    if (!membershipFound && !linkedAccount) {
      await authenticateToContinueSession({
        reason:
          LOGOUT_REASON.AUTHENTICATED_USER
            .MISSING_LINKED_ACCOUNT_AND_MEMBERSHIP,
      });
      return;
    }

    if (membershipFound) setMatchedMembership(membershipFound);
    if (linkedAccount) {
      setMatchedAccount(linkedAccount);
      sendInfoLog(BookingInfo.LINKED_ACCOUNT_FOUND, {
        consumerID: consumer.id,
        linkedAccountID: linkedAccount.id,
      });
    }

    setUserAccountData({
      consumerID: consumer.id,
      linkedAccountID: linkedAccount?.id,
      membershipID: membershipFound?.id,
    });

    setClientData({
      issuer: membershipFound?.membershipPlan?.issuer as string | undefined,
      planName: membershipFound?.membershipPlan?.planName as string | undefined,
    });

    setItem(IS_USER_VALIDATED, 'true');
    setIsUserValidated(true);
  };

  const handleUserValidation = async () => {
    if (!shouldValidateUser) return;

    if (inFlightRef.current) {
      await inFlightRef.current;
      return;
    }

    const run = runUserValidation().finally(() => {
      inFlightRef.current = null;
    });
    inFlightRef.current = run;
    await run;
  };

  return {
    handleUserValidation,
    isUserValidated,
    shouldValidateUser,
    userValidationDeps,
    userValidationError: fetchConsumerError,
  };
};
